<template>
  <div
    class="ca-header__mobile-search ds-scope"
    :class="{ open: open, closing: closing, 'keyboard-open': keyboardOpen }"
    :style="keyboardOpen ? { height: visualHeight + 'px' } : {}"
    @touchmove.prevent
  >
    <div
      class="ca-header__mobile-search-top px-size-5 py-size-4 mt-size-4 mb-size-4"
    >
      <div
        class="ca-header__search-bar gap-size-3 rounded-xs py-size-2 px-size-3 is-open--mobile js-search-area"
      >
        <input
          id="globalSearchMobile"
          type="text"
          ref="mobileSearchInputRef"
          class="ca-header__search-bar-input px-size-2 py-size-0 caption-medium js-search-area"
          placeholder="Procurar no site"
          :value="searchQuery"
          @input="emit('update:searchQuery', $event.target.value)"
          @focus="emit('open-search-results')"
          @keydown.enter="emit('submit-search')"
        />
        <button
          v-show="searchQuery"
          class="ca-header__search-bar-close js-search-area"
          @click="emit('clear-search')"
          aria-label="Limpar pesquisa"
        >
          <component :is="SearchCloseIcon" />
        </button>
        <button
          class="ca-header__searchbtn rounded-xs js-search-area"
          aria-label="Pesquisar"
          @click="emit('submit-search')"
        >
          <component :is="SearchIcon" />
        </button>
      </div>
      <button
        class="ca-header__mobile-search-close rounded-sm"
        @click="emit('close')"
        aria-label="Fechar pesquisa"
      >
        <component :is="Close" />
      </button>
    </div>

    <!-- Results inside mobile search overlay -->
    <div
      v-if="searchResultsOpen"
      class="ca-header__mobile-search-results js-search-area"
      :class="{ 'is-keyboard-open': keyboardOpen }"
    >
      <hr
        v-if="keyboardOpen && autocompleteResults.length > 0"
        class="ca-header__search-separator"
      />

      <div
        class="ca-header__megamenu-inner px-size-5 py-size-0"
        :class="{ 'ca-header__megamenu-inner--loading': autocompleteLoading }"
        @touchmove.stop
      >
        <ul v-if="autocompleteLoading" class="ca-header__megamenu-list">
          <li v-for="n in 6" :key="n">
            <div
              class="ca-header__megamenu-link gap-size-3 px-size-5 py-size-2 caption-large"
            >
              <span class="ca-header__autocomplete-shimmer" />
            </div>
          </li>
        </ul>

        <ul
          v-else-if="searchQuery.trim().length < 3 && suggestions.length"
          class="ca-header__search-list gap-size-3 p-size-0 margin-size-0"
        >
          <li
            v-for="(item, idx) in suggestions.slice(0, 6)"
            class="ca-header-search__megamenu-results gap-size-3 ca-header-search__megamenu-results--mobile"
            :key="idx"
            @click="emit('select-suggestion', item)"
          >
            <component :is="SearchIconGrey" />
            <a
              class="ca-header__megamenu-link gap-size-3 px-size-5 py-size-2 caption-large"
              style="cursor: pointer"
            >
              <span>{{ item.title }}</span>
            </a>
          </li>
        </ul>

        <ul
          v-else-if="autocompleteResults.length"
          class="ca-header__search-list gap-size-3 p-size-0 margin-size-0"
        >
          <li
            v-for="(result, idx) in autocompleteResults.slice(0, 6)"
            class="ca-header-search__megamenu-results gap-size-3 ca-header-search__megamenu-results--mobile"
            :key="idx"
            @click="emit('select-suggestion', result)"
          >
            <component :is="SearchIconGrey" />
            <a
              class="ca-header__megamenu-link gap-size-3 px-size-5 py-size-2 caption-large"
              style="cursor: pointer"
            >
              <span>
                {{ highlight(result.label).matched
                }}<span style="font-weight: 700">{{
                  highlight(result.label).unmatched
                }}</span>
              </span>
            </a>
          </li>
        </ul>

        <ul v-else>
          <li
            class="ca-header-search__megamenu-results gap-size-3 ca-header-search__megamenu-results--mobile"
          >
            <span class="ca-header__search__no-result caption-medium"
              >Nenhum resultado...</span
            >
          </li>
        </ul>
      </div>
      <!-- hr only visible when keyboard is open -->
      <hr
        v-if="autocompleteResults.length > 6"
        class="ca-header__search-separator"
        style="margin: 0 var(--size-4)"
      />

      <div
        class="ca-header__search-view-all-wrapper px-size-5 py-size-3"
        v-if="autocompleteResults.length > 6"
      >
        <LinkButton
          type="default"
          @click="emit('show-all')"
          label="Ver todos os resultados"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import SearchIcon from "@assets/search-icon.svg";
import SearchIconGrey from "@assets/search-icon-grey.svg";
import SearchCloseIcon from "@assets/search-close.svg";
import { LinkButton } from "ca-online-design-system";
import Close from "@assets/fechar.svg";
import { ref, watch, nextTick, onMounted, onUnmounted } from "vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  closing: { type: Boolean, default: false },
  searchQuery: { type: String, default: "" },
  searchResultsOpen: { type: Boolean, default: false },
  autocompleteLoading: { type: Boolean, default: false },
  suggestions: { type: Array, default: () => [] },
  autocompleteResults: { type: Array, default: () => [] },
  highlightMatchedText: { type: Function, required: true },
});

const emit = defineEmits([
  "close",
  "clear-search",
  "submit-search",
  "open-search-results",
  "select-suggestion",
  "show-all",
  "update:searchQuery",
]);

const mobileSearchInputRef = ref(null);
const keyboardOpen = ref(false);
const visualHeight = ref(window.visualViewport?.height ?? window.innerHeight);
const scrollY = ref(0);

const lockBodyScroll = () => {
  scrollY.value = window.scrollY;
  document.body.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.top = `-${scrollY.value}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
};

const unlockBodyScroll = () => {
  document.body.style.overflow = "";
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  window.scrollTo(0, scrollY.value);
};

const updateViewport = () => {
  const newHeight = window.visualViewport?.height ?? window.innerHeight;
  keyboardOpen.value = window.innerHeight - newHeight > 150;
  visualHeight.value = newHeight;
};

onMounted(() => {
  window.visualViewport?.addEventListener("resize", updateViewport);
});

onUnmounted(() => {
  unlockBodyScroll();
  window.visualViewport?.removeEventListener("resize", updateViewport);
});

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      lockBodyScroll();
      nextTick(() => {
        mobileSearchInputRef.value?.focus();
      });
    } else {
      unlockBodyScroll();
      keyboardOpen.value = false;
    }
  },
);

const highlight = (label) =>
  props.highlightMatchedText(label, props.searchQuery);
</script>

<style scoped lang="scss">
.ca-header__mobile-search {
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: 9999;
  height: 100dvh;
  overflow: hidden;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  visibility: hidden;
  touch-action: none;

  &.open {
    display: flex;
    transform: translateX(0);
    pointer-events: all;
    visibility: visible;
  }

  &.closing {
    display: flex;
    transform: translateX(100%);
    pointer-events: none;
  }

  &.keyboard-open {
    box-shadow: 0 500px 0 500px white;
  }
}

.ca-header__mobile-search-top {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.ca-header__mobile-search-close {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--ca-color-hover-pressed-container-high-hover);
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
  }
}

.ca-header__mobile-search-results {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .ca-header__megamenu-inner {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;

    &--loading {
      padding: var(--size-0);
    }

    ul {
      padding-inline-start: 0;
      list-style: none;
    }
  }
}

.ca-header__search-separator {
  border: none;
  border-top: 1px solid var(--ca-color-text-light);
  margin: 0;
  flex-shrink: 0;
}

.ca-header__search-view-all-wrapper {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  background: white;
}

.ca-header__search-bar {
  display: flex;
  align-items: center;
  border: 1px solid transparent;
  max-height: 48px;
  background: white;

  &.is-open--mobile {
    flex: 1 1 auto;
    max-width: 100%;
    width: 100%;
    min-width: 0;
    border: 1px solid var(--ca-color-theme-primary);
  }

  &-input {
    order: 1;
    flex: 1 1 auto;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    caret-color: var(--ca-color-theme-primary);
    color: var(--ca-color-text-body);
    letter-spacing: var(--letter-spacing-sm);

    &::placeholder {
      color: var(--ca-color-text-body-variant);
    }
  }

  &-close {
    order: 2;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 0;
    margin-left: auto;
    cursor: pointer;

    &:focus-visible {
      outline: 1px solid var(--ca-color-feedback-info);
    }
  }
}

.ca-header__searchbtn {
  flex-shrink: 0;
  order: -1;
  background: white;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--ca-color-hover-pressed-neutral-hover);
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
  }
}

.ca-header__megamenu-separator {
  border: none;
  border-top: 1px solid var(--ca-color-text-light);
}

.ca-header__megamenu-list,
.ca-header__search-list {
  list-style: none;
  display: flex;
  flex-direction: column;
}

.ca-header__search-list {
  overflow-y: scroll;
}

.ca-header-search__megamenu-results {
  display: flex;
  align-items: center;
  list-style: none;
  padding-left: var(--size-0) !important;

  &--mobile {
    padding-left: var(--size-0) !important;

    &:active {
      background: var(--ca-color-hover-pressed-inverted-hover-light);
      border-radius: var(--size-3);
    }
  }
}

.ca-header__megamenu-link {
  color: var(--ca-color-text-body-variant);
  letter-spacing: var(--letter-spacing-xs);
  text-decoration: none;
  display: flex;
  width: 100%;
  align-items: center;
  min-height: 40px;
  cursor: pointer;
}

.ca-header__megamenu-view-all {
  color: var(--ca-color-theme-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-sm);
  letter-spacing: var(--letter-spacing-sm);
  display: flex;
  align-items: center;
  background: white;
  border: none;
  cursor: pointer;

  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
  }
}

.ca-header__search__no-result {
  color: var(--ca-color-text-body-variant);
}

@keyframes shimmer {
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
}

.ca-header__autocomplete-shimmer {
  display: inline-block;
  height: 40px;
  width: 100%;
  border-radius: var(--size-2);
  background: linear-gradient(
    270deg,
    #fcfdfb 0%,
    var(--Colors-CAEmpresas-SolarizedMedium, #edecea) 50%,
    #fcfdfb 100%
  );
  background-size: 200% auto;
  animation: shimmer 2.5s linear infinite;
}
</style>
